import { message } from 'antd';
import axios from 'axios';
import { logoutUser } from './store/reducers/sessionReducer';
import store from './store/store';
import history from './utils/history';

let isHandling401Error = false;

//==============================================================
// ✅ Utility: Print startup block with env vars
//==============================================================
function printStartupEnvBlock() {
    if (!['local', 'development'].includes(process.env.NODE_ENV)) return;

    console.log('==============================================================');
    console.log('✅ eCoachManagerUI started...');
    console.log('==============================================================');

    const allEnv = { ...process.env, ...window._env_ };
    Object.entries(allEnv).forEach(([key, value]) => {
        if (key.toUpperCase().includes('KEY')) {
            console.log(`${key} = ********************`);
        } else {
            console.log(`${key} = ${value}`);
        }
    });

    console.log('==============================================================');
}

export default function eCoachVMSApi() {
    //==============================================================
    // ✅ Resolve API URL
    //==============================================================
    const apiUrlRaw = window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
    const apiUrl = apiUrlRaw?.trim();

    // printStartupEnvBlock();

    let apiHost;
    try {
        apiHost = new URL(apiUrl).hostname;
    } catch (err) {
        console.warn('[Api.js] Failed to parse apiUrl:', apiUrl, err);
        apiHost = undefined;
    }

    //==============================================================
    // ✅ Axios instance
    //==============================================================
    const baseURL = axios.create({
        baseURL: apiUrl,
        headers: {
            'Content-Type': 'application/json'
        },
        withCredentials: true
    });

    console.log('[Api.js] Created axios instance with baseURL =', baseURL.defaults.baseURL);

    //==============================================================
    // ✅ Helpers
    //==============================================================
    const shouldSkipInterceptor = (url) => {
        const skipRoutes = ['/login', '/logout', '/loginvms'];
        return skipRoutes.some((route) => url.includes(route));
    };

    const clientHostname = window.location.hostname;
    const isLocalClient = ['localhost', '127.0.0.1', '::1'].includes(clientHostname);

    //==============================================================
    // ✅ Request Interceptor
    //==============================================================
    baseURL.interceptors.request.use(
        (config) => {
            console.log('[Api.js] → REQUEST', {
                url: config.url,
                method: config.method,
                baseURL: config.baseURL,
                headers: config.headers
            });

            config.headers = config.headers || {};

            let xTenantHost;
            let xClientHost;

            if (isLocalClient && apiHost) {
                xTenantHost = apiHost;
                xClientHost = apiHost;
                config.headers['X-Tenant-Host'] = xTenantHost;
                config.headers['X-Client-Hostname'] = xClientHost;
            } else if (clientHostname) {
                xTenantHost = clientHostname;
                xClientHost = clientHostname;
                config.headers['X-Client-Hostname'] = xClientHost;
            }

            console.log('[Api.js] Final Request Headers =', config.headers);
            return config;
        },
        (error) => {
            console.error('[Api.js] REQUEST ERROR', error);
            return Promise.reject(error);
        }
    );

    //==============================================================
    // ✅ Response Interceptor
    //==============================================================
    baseURL.interceptors.response.use(
        (res) => {
            console.log('[Api.js] ← RESPONSE OK', {
                url: res.config?.url,
                status: res.status,
                data: res.data
            });
            return res;
        },
        async (err) => {
            console.error('[Api.js] ← RESPONSE ERROR', {
                url: err.config?.url,
                status: err.response?.status,
                data: err.response?.data
            });

            const originalRequest = err.config;

            if (shouldSkipInterceptor(originalRequest.url)) {
                return Promise.reject(err);
            }

            if (err.response?.status === 403) {
                message.error('You are not allowed to access this page!', 5);
                history.back();
            }

            if (err.response?.status === 401 || err.response?.data?.logout) {
                if (isHandling401Error) {
                    return Promise.resolve(err);
                }
                isHandling401Error = true;
                try {
                    const session = localStorage.getItem('session') || sessionStorage.getItem('session');
                    const isWorkshop = session?.startsWith('workshop') || false;
                    const redirectTo = isWorkshop ? '/service' : '/login';
                    message.error('Your session has expired. Please login again.', 5);
                    await store.dispatch(logoutUser());

                    setTimeout(() => {
                        history.push(redirectTo);
                        isHandling401Error = false;
                    }, 500);

                    return Promise.resolve({ data: { redirected: true } });
                } catch (e) {
                    isHandling401Error = false;
                    console.warn('Error handling session expiration:', e);
                }
            }
            return Promise.reject(err);
        }
    );

    return { baseURL };
}
